import React from 'react';
import { Shield, Clipboard, Thermometer, CheckCircle, FileText, Award, Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Contact } from './Contact';

export const MedicalCourier = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const procedures = [
    {
      icon: Shield,
      title: "PHIPA-Aware Confidentiality Standards",
      description: "We maintain strict confidentiality procedures in accordance with Ontario's Personal Health Information Protection Act (PHIPA).",
      points: [
        "No disclosure of patient or medical information",
        "Secure handling of documentation",
        "Immediate reporting of any potential privacy concerns",
        "Professional conduct within healthcare facilities"
      ]
    },
    {
      icon: Clipboard,
      title: "Chain of Custody & Accountability",
      description: "Every medical shipment is handled under a documented chain-of-custody process:",
      points: [
        "Verification of authorized pickup personnel",
        "Documentation of pickup time and package count",
        "Secure in-transit handling",
        "Direct handoff to authorized receiving staff",
        "Signed proof of delivery"
      ]
    },
    {
      icon: Thermometer,
      title: "Temperature-Controlled Transport",
      description: "For temperature-sensitive materials, we utilize:",
      points: [
        "Insulated transport containers",
        "Secure cooler placement",
        "Climate-controlled vehicle transport",
        "Optional temperature logging upon request"
      ]
    },
    {
      icon: FileText,
      title: "Secure Specimen Handling",
      description: "We follow strict handling procedures for medical specimens and healthcare materials:",
      points: [
        "No opening or tampering with sealed packages",
        "Immediate reporting of any visible damage",
        "Clean, secure cargo environment",
        "No mixing with unrelated freight"
      ]
    },
    {
      icon: CheckCircle,
      title: "Proof of Delivery & Documentation",
      description: "Every delivery includes:",
      points: [
        "Time-stamped confirmation",
        "Recipient name documentation",
        "Signed delivery verification",
        "Retained records for accountability"
      ]
    },
    {
      icon: Award,
      title: "Professional Training & Compliance",
      description: "Our medical courier operators maintain:",
      points: [
        "TDG (Transportation of Dangerous Goods) certification (as applicable)",
        "PHIPA awareness training",
        "Clean driver abstracts",
        "Criminal background screening (where required)"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="text-orange-400" size={16} />
              <span className="text-orange-300 text-sm font-medium">PHIPA Compliant Medical Courier</span>
            </div>

            {/* Heading */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Medical Courier <span className="text-orange-500">Procedures & Compliance</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              At Shaw's Transport & Logistics, we operate under structured medical courier procedures designed to ensure secure, compliant, and time-sensitive transport of healthcare materials across Ontario.
            </p>

            <p className="text-base md:text-lg text-gray-400 mb-10">
              We understand that medical deliveries require precision, confidentiality, and accountability. Our team follows documented operating protocols to maintain the highest service standards.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6 group"
              >
                Request Medical Courier Quote
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
                onClick={() => window.location.href = 'tel:6479934955'}
              >
                <Phone className="mr-2" size={20} />
                Call 647-993-4955
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Procedures Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Professional <span className="text-orange-500">Standards & Procedures</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We approach medical logistics with professionalism, reliability, and operational discipline.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {procedures.map((procedure, index) => {
                const IconComponent = procedure.icon;
                return (
                  <Card 
                    key={index}
                    className="border-2 hover:border-orange-500 transition-all duration-300 hover:shadow-xl"
                  >
                    <CardHeader>
                      <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="text-orange-500" size={32} />
                      </div>
                      <CardTitle className="text-2xl text-gray-900">
                        {procedure.title}
                      </CardTitle>
                      <CardDescription className="text-base text-gray-600 leading-relaxed mt-2">
                        {procedure.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {procedure.points.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border-2 border-orange-500">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
                Why Healthcare Facilities Trust Shaw's Transport
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Time-Sensitive Reliability</h4>
                    <p className="text-gray-600 text-sm">STAT deliveries handled with urgency and precision</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">24/7 Availability</h4>
                    <p className="text-gray-600 text-sm">Round-the-clock service for emergency medical transport</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Dedicated Medical Routes</h4>
                    <p className="text-gray-600 text-sm">Regular scheduled runs to healthcare facilities</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Professional Drivers</h4>
                    <p className="text-gray-600 text-sm">Trained, screened, and PHIPA-aware personnel</p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  onClick={scrollToContact}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg group"
                >
                  Get Started with Medical Courier Services
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality Notice */}
      <section className="py-12 bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="text-orange-500 mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold text-white mb-3">
              Confidentiality is Our Priority
            </h3>
            <p className="text-gray-300 leading-relaxed">
              We treat every medical shipment with the utmost confidentiality and professionalism. Our procedures are designed to protect patient privacy and maintain the integrity of healthcare operations across Ontario.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <Contact />
    </div>
  );
};
